import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';

/**
 * <HeroVideo />
 *
 * Reusable, accessible hero-band video player tuned for marketing pages.
 * Ships with the browser-safe autoplay recipe (muted + inline + loop),
 * respects the OS reduced-motion preference, and exposes a compact
 * tap-to-unmute + play/pause control cluster in the bottom-right.
 *
 * Behavior:
 *   • Autoplay muted + loop + playsInline (Chrome/Safari/Firefox allow
 *     this without user gesture; iOS 12+ requires playsInline).
 *   • If the visitor prefers reduced motion, we skip autoplay entirely
 *     and show the poster frame + a Play button they must tap.
 *   • Clicking anywhere on the video toggles play/pause. The dedicated
 *     control buttons are for accessibility & discoverability.
 *   • Mute is the default; a prominent "Tap for sound" button invites
 *     the visitor to unmute. Once unmuted, the label swaps to "Sound
 *     on" so the state is unambiguous.
 *
 * Design decisions:
 *   • 16:9 aspect ratio locked via `aspect-video` so layout never jumps.
 *   • Rounded-3xl + subtle border + shadow-2xl for a premium look on
 *     top of any background (dark or light).
 *   • object-cover so the video always fills the container edge-to-edge.
 *     Videos wider or taller than 16:9 will be cropped rather than
 *     letterboxed — most brand animations tolerate this well.
 */

const DEFAULT_VIDEO_SRC_MP4 = '/videos/splashpad_hero.mp4';
const DEFAULT_VIDEO_SRC_WEBM = '/videos/splashpad_hero.webm';
const DEFAULT_POSTER = '/videos/splashpad_hero_poster.jpg';

export const HeroVideo = ({
    src,
    poster = DEFAULT_POSTER,
    className = '',
    ariaLabel = 'SplashPad promotional video: designed for instructors by an instructor.',
    preload = 'auto',
    dataTestId = 'hero-video',
}) => {
    const { t: t42 } = useLocaleR42();
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    const [playing, setPlaying] = useState(true);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [errored, setErrored] = useState(false);

    // Honor OS-level reduced-motion preference. Users who opt into
    // reduced motion see the poster frame + a Play button they can
    // tap explicitly; we never autoplay motion for them.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mql.matches);
        const listener = (e) => setPrefersReducedMotion(e.matches);
        if (mql.addEventListener) mql.addEventListener('change', listener);
        else mql.addListener(listener);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', listener);
            else mql.removeListener(listener);
        };
    }, []);

    // Wire the DOM video element's play/pause events back to React state
    // so the button icons stay accurate even if playback is affected by
    // browser policy (e.g., background tab paused, low-power mode, …).
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return undefined;
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        const onError = () => setErrored(true);
        v.addEventListener('play', onPlay);
        v.addEventListener('pause', onPause);
        v.addEventListener('error', onError);
        return () => {
            v.removeEventListener('play', onPlay);
            v.removeEventListener('pause', onPause);
            v.removeEventListener('error', onError);
        };
    }, []);

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => { /* browser blocked */ });
        } else {
            v.pause();
        }
    };

    const toggleMute = (e) => {
        // Ensure clicking mute doesn't also toggle play (both handlers live
        // inside the same container).
        e.stopPropagation();
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
        // Ensure playback continues when the coach unmutes (some browsers
        // treat unmute as a fresh user-gesture that requires an explicit
        // play() call to un-suspend).
        if (!v.muted && v.paused) {
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        }
    };

    // Note: we no longer aggressively hide the whole component on the
    // first `error` event. Chrome fires transient errors during range
    // requests / codec probes on fresh loads which would otherwise
    // silently unmount our hero video. We keep the error state around
    // for diagnostics but always render the <video> element and let the
    // browser fall back naturally (e.g., poster only) if it truly
    // can't decode.
    void errored;

    return (
        <div
            className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-black shadow-2xl ${className}`}
            data-testid={dataTestId}
        >
            <div className="aspect-video w-full">
                <video
                    ref={videoRef}
                    poster={poster}
                    autoPlay={!prefersReducedMotion}
                    muted
                    loop
                    playsInline
                    preload={preload}
                    aria-label={ariaLabel}
                    onClick={togglePlay}
                    className="h-full w-full cursor-pointer object-cover"
                    data-testid={`${dataTestId}-el`}
                >
                    {/* WebM (VP9 + Opus) is royalty-free and plays in every
                        modern browser \u2014 including Chromium builds that ship
                        without proprietary codecs (Playwright, some Linux
                        distros). MP4 (H.264 + AAC) is the fallback for
                        Safari. If a custom `src` is passed, we render just
                        that one source instead. */}
                    {src ? (
                        <source src={src} />
                    ) : (
                        <>
                            <source src={DEFAULT_VIDEO_SRC_WEBM} type="video/webm" />
                            <source src={DEFAULT_VIDEO_SRC_MP4} type="video/mp4" />
                        </>
                    )}
                </video>
            </div>

            {/* Control cluster — bottom-right so it never covers the mascot
                which lives in the upper portion of the frame. */}
            <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-3 sm:p-4">
                <div className="pointer-events-auto flex items-center gap-2">
                    <button
                        type="button"
                        onClick={togglePlay}
                        aria-label={playing ? t42("Pause video") : t42("Play video")}
                        aria-pressed={!playing}
                        className="sd-focus inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition-colors hover:bg-black/70"
                        data-testid={`${dataTestId}-play-toggle`}
                    >
                        {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                    </button>
                    <button
                        type="button"
                        onClick={toggleMute}
                        aria-label={muted ? t42("Unmute video") : t42("Mute video")}
                        aria-pressed={!muted}
                        className="sd-focus inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-black/50 px-3 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-black/70"
                        data-testid={`${dataTestId}-mute-toggle`}
                    >
                        {muted ? (
                            <>
                                <VolumeX className="h-4 w-4" aria-hidden="true" /> {t42("Tap for sound")} </>
                        ) : (
                            <>
                                <Volume2 className="h-4 w-4" aria-hidden="true" /> {t42("Sound on")} </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HeroVideo;
